import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook } from 'lucide-react';
import { Logo } from '@/components/Header';
import { useTranslation } from 'react-i18next';

const TikTokIcon = ({ className = '', ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    {...props}
  >
    <path d="M15 4v8.5a3.5 3.5 0 1 1-3.5-3.5" />
    <path d="M15 4a5 5 0 0 0 5 5" />
  </svg>
);

const SOCIAL_LINKS = [
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/ridalo.co?stkn=Y3U1aDN3aGcxaXIw&utm_source=qr',
    Icon: Instagram,
    testId: 'footer-social-instagram',
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/share/1CHRCAkBw2/?mibextid=wwXIfr',
    Icon: Facebook,
    testId: 'footer-social-facebook',
  },
  {
    name: 'TikTok',
    href: 'https://www.tiktok.com/@ridalo.co?_r=1&_t=ZS-9A4ysb40NEr',
    Icon: TikTokIcon,
    testId: 'footer-social-tiktok',
  },
];

export const Footer = () => {
  const { t } = useTranslation();

  return (
  <footer className="bg-primary text-white/80 pt-12 pb-8" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
        <div className="col-span-2 md:col-span-1">
          <Logo variant="dark-bg" />
          <p className="text-sm text-white/60 mt-4 max-w-xs">
            {t('footer.tagline')}
          </p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">{t('footer.services')}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/scooters" className="hover:text-accent transition-colors inline-flex items-center max-lg:min-h-10" data-testid="footer-link-scooters">{t('footer.scooters')}</Link></li>
            <li><Link to="/transport" className="hover:text-accent transition-colors inline-flex items-center max-lg:min-h-10" data-testid="footer-link-transfers">{t('footer.transfers')}</Link></li>
            <li><Link to="/services?category=excursion" className="hover:text-accent transition-colors inline-flex items-center max-lg:min-h-10" data-testid="footer-link-excursions">{t('footer.excursions')}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">{t('footer.company')}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-accent transition-colors inline-flex items-center max-lg:min-h-10" data-testid="footer-link-about">{t('header.about')}</Link></li>
            <li><Link to="/faq" className="hover:text-accent transition-colors inline-flex items-center max-lg:min-h-10" data-testid="footer-link-faq">{t('header.faq')}</Link></li>
            <li><Link to="/contact" className="hover:text-accent transition-colors inline-flex items-center max-lg:min-h-10" data-testid="footer-link-contact">{t('header.contact')}</Link></li>
            <li><Link to="/register?role=provider" className="hover:text-accent transition-colors inline-flex items-center max-lg:min-h-10" data-testid="footer-link-become-provider">{t('header.becomeProvider')}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">{t('footer.legal')}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/conditions-generales" className="hover:text-accent transition-colors inline-flex items-center max-lg:min-h-10" data-testid="footer-link-terms">{t('footer.termsOfService')}</Link></li>
            <li><Link to="/politique-confidentialite" className="hover:text-accent transition-colors inline-flex items-center max-lg:min-h-10" data-testid="footer-link-privacy">{t('footer.privacyPolicy')}</Link></li>
            <li><Link to="/mentions-legales" className="hover:text-accent transition-colors inline-flex items-center max-lg:min-h-10" data-testid="footer-link-legal">{t('footer.legalNotice')}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 pt-6 pb-6 flex flex-col sm:flex-row items-center sm:justify-between gap-4" data-testid="footer-social">
        <span className="text-sm text-white/60 font-medium">{t('footer.followUs')}</span>
        <ul className="flex items-center gap-4">
          {SOCIAL_LINKS.map(({ name, href, Icon, testId }) => (
            <li key={name}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={name}
                title={name}
                data-testid={testId}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 text-white/70 hover:text-accent hover:border-accent hover:bg-white/5 transition-colors"
              >
                <Icon className="w-5 h-5" />
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="border-t border-white/10 pt-6 text-center text-sm text-white/50">
        {t('footer.copyright')}
      </div>
    </div>
  </footer>
  );
};
